import {
  Grid,
  makeStyles,
  Theme,
  Button,
  Avatar,
  Typography,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { RecordHeader } from "@ofqual/portal-components/components/RecordHeader";
import { NonNullableItemState } from "../../redux/state";
import {
  formatUtcDate,
  standardDateFormats,
} from "@ofqual/portal-core/core/utils/dateUtils";
import { SarsStatus } from "../../domain/SarsModel";

import { AssignDialog } from "./Dialogs/AssignDialog";
import { useAuth } from "@ofqual/portal-core/core/hooks";
import { authSelector } from "../../redux/selectors";
import { UserType } from "../../redux/types";
import SwapVertIcon from "@material-ui/icons/SwapVert";
import { ActionSelectorDialog } from "./Dialogs/transferDialogs/ActionSelectorDialog";

const useStyles = makeStyles((theme: Theme) => {
  return {
    gridItem: {
      borderRight: "1px solid rgba(255,255,255,0.2)",
      "& div": {
        padding: "4px",
      },
    },
    textColor: {
      color: "#f5f5f5",
    },
  };
});

export type ItemHeaderProps = {
  state: NonNullableItemState | null;
};

export const ItemHeader: React.FC<ItemHeaderProps> = ({ state }) => {
  const classes = useStyles();

  const { userType } = useAuth(authSelector);

  const canAssignOrTransfer = userType === UserType.Ofqual;

  const [isDialogOpen, setIsOpenAssignDialog] = useState(false);
  const [allowReassign, setAllowReassign] = useState(false);
  const [actionSelectorDialog, setActionSelectorDialog] = useState<boolean>(
    false
  );

  useEffect(() => {
    if (!!state && !!state.item) {
      if (
        state.item.status === SarsStatus.Closed ||
        state.item.status === SarsStatus.Rejected
      ) {
        setAllowReassign(false);
      } else {
        setAllowReassign(true);
      }
    }
  }, [state, setAllowReassign]);

  const assignedDisplayName =
    userType === UserType.Ofqual ? state?.item?.caseLeadDisplayName : "";
  const assignedToUpn =
    userType === UserType.Ofqual ? state?.item?.caseLeadUPN : "";

  return (
    <>
      <RecordHeader>
        <Grid
          container
          direction="row"
          justify="flex-start"
          alignItems="stretch"
          spacing={2}
          style={{ height: "100%" }}
        >
          <Grid className={classes.gridItem}>
            <div>
              <label>
                <strong>Query id: </strong>
              </label>
              <span data-test="metadata-query-reference-number">
                {state?.item?.referenceNumber}
              </span>
            </div>
            <div>
              <label>
                <strong>Created by: </strong>
              </label>
              <span data-test="metadata-created-by">
                {state?.item?.createdByDisplayName}
              </span>
            </div>
            <div>
              <label>
                <strong>Created at: </strong>
              </label>
              <span data-test="metadata-created-at">
                {formatUtcDate(
                  state?.item?.createdDate,
                  standardDateFormats.DATE_TIME
                )}
              </span>
            </div>
          </Grid>
          <Grid className={classes.gridItem}>
            <div>
              <label>
                <strong>Status: </strong>
              </label>
              <span data-test="metadata-status">
                {SarsStatus[state?.item?.status || 0]}
              </span>
            </div>
          </Grid>
          {canAssignOrTransfer && (
            <>
              <Grid className={classes.gridItem}>
                <Button
                  onClick={() => setIsOpenAssignDialog(true)}
                  data-test="sar-assigned-to"
                  style={{ color: "#f5f5f5" }}
                >
                  {renderAssignButtonContent(
                    assignedDisplayName,
                    assignedToUpn
                  )}
                </Button>
              </Grid>
              <Grid className={classes.gridItem}>
                <Button
                  startIcon={<SwapVertIcon />}
                  className={classes.textColor}
                  onClick={() => setActionSelectorDialog(true)}
                  data-test="transfer-foi-button"
                >
                  <Typography variant="h6" className={classes.textColor}>
                    Transfer
                  </Typography>
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </RecordHeader>
      <AssignDialog
        sar={state?.item!}
        onClose={() => setIsOpenAssignDialog(false)}
        open={isDialogOpen}
        userType={userType}
        allowReassign={allowReassign}
      />
      <ActionSelectorDialog
        itemHeaderProps={{ state: state }}
        onClose={() => setActionSelectorDialog(false)}
        open={actionSelectorDialog}
        disabled={!allowReassign}
      />
    </>
  );
};

const renderAssignButtonContent = (
  assignedDisplayName?: string | null,
  assignedUpn?: string | null
) => {
  const calculateInitials = (name: string): string => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("");
  };
  return (
    <>
      <Avatar
        style={{
          backgroundColor: "#293445",
          width: 70,
          height: 70,
          fontSize: 30,
        }}
      >
        {assignedDisplayName ? calculateInitials(assignedDisplayName) : ""}
      </Avatar>
      {assignedUpn ? (
        <div>
          <label>
            <strong>Case Leader:</strong>
          </label>
          <br></br>
          <span data-test="metadata-assigned-to">
            {assignedDisplayName
              ? assignedDisplayName
              : assignedUpn
              ? assignedUpn
              : "Case unassigned"}
          </span>
        </div>
      ) : (
        <div>
          <label>
            <strong>Assign case to</strong>
          </label>
        </div>
      )}
    </>
  );
};
